<template>
  <div class="marketplace-checkout--order-confirmation">
    <div class="marketplace-checkout--order-confirmation-wrapper">
      <div class="flex flex-col items-start gap-sm">
        <p class="m-0 text-sm c-gray-700">{{ 'Order #' | t }}{{ orderResponse?.code | formatOrderNumber }}</p>
        <h2 class="text-xl fw-strong">
          {{ 'Thank you' | t }}{{ orderResponse?.user?.name === 'guest' ? '' : ' ' + orderResponse?.user?.name }}!
        </h2>
      </div>
      <div class="flex flex-col items-start gap-sm mt-lg">
        <h2 class="text-xl fw-strong">{{ 'Your order is confirmed' | t }}</h2>
        <p class="m-0">{{ 'You will receive a confirmation email shortly.' | t }}</p>
      </div>
      <div class="marketplace-checkout--confirmation">
        <div>
          <p class="flex flex-col items-start gap-sm c-gray-700">
            <span class="text-base fw-strong c-gray-900">{{ 'Contact' | t }}</span>
            <span>{{ orderResponse?.user?.uid }}</span>
          </p>
        </div>
        <div>
          <div class="flex flex-col items-start gap-sm c-gray-700">
            <span class="text-base fw-strong c-gray-900">{{ 'Payment' | t }}</span>
            <span>
              <SloIcon
                :name="orderResponse?.paymentInfo?.cardType?.name | getCardTypeIconName"
                size="lg"
              />
              <span class="text-capitalize">{{ orderResponse?.paymentInfo?.cardType?.code }}</span>
              ••••
              {{ orderResponse?.paymentInfo?.cardNumber | getCardNumberLastFourDigits }}
            </span>
          </div>
        </div>
        <div>
          <div class="flex flex-col items-start gap-sm">
            <p class="text-base fw-strong">{{ 'Shipping Address' | t }}</p>
            <div class="c-gray-700">
              <p>{{ orderResponse?.deliveryAddress?.firstName }}</p>
              <p v-if="orderResponse?.deliveryAddress?.attention">
                ATTN: {{ orderResponse?.deliveryAddress?.attention }}
              </p>
              <p v-if="orderResponse?.deliveryAddress?.companyName">
                {{ orderResponse?.deliveryAddress?.companyName }}
              </p>
              <p>{{ orderResponse?.deliveryAddress?.line1 }}</p>
              <p>{{ orderResponse?.deliveryAddress?.line2 }}</p>
              <p>
                {{ orderResponse?.deliveryAddress?.town }},
                {{ orderResponse?.deliveryAddress?.region?.isocodeShort || orderResponse?.deliveryAddress?.region?.isocode }}
              </p>
              <p>
                {{ orderResponse?.deliveryAddress?.postalCode }},
                {{ orderResponse?.deliveryAddress?.country?.isocode }}
              </p>
            </div>
          </div>
        </div>
        <div>
          <div class="flex flex-col items-start gap-sm">
            <p class="text-base fw-strong">{{ 'Billing Address' | t }}</p>
            <div class="c-gray-700">
              <p>{{ orderResponse?.paymentInfo?.billingAddress?.firstName }}</p>
              <p v-if="orderResponse?.paymentInfo?.billingAddress?.lastName">
                {{ orderResponse?.paymentInfo?.billingAddress?.lastName }}
              </p>
              <p>{{ orderResponse?.paymentInfo?.billingAddress?.line1 }}</p>
              <p>{{ orderResponse?.paymentInfo?.billingAddress?.line2 }}</p>
              <p>
                {{ orderResponse?.paymentInfo?.billingAddress?.town }},
                {{ orderResponse?.paymentInfo?.billingAddress?.region?.isocodeShort || orderResponse?.paymentInfo?.billingAddress?.region?.isocode }}
              </p>
              <p>
                {{ orderResponse?.paymentInfo?.billingAddress?.postalCode }},
                {{ orderResponse?.paymentInfo?.billingAddress?.country?.isocode }}
              </p>
            </div>
          </div>
        </div>
        <div :class="!orderResponse?.purchaseOrderNumber ? 'grid-column-full-width' : ''">
          <div class="flex flex-col items-start gap-sm">
            <p class="text-base fw-strong">{{ 'Shipping Method' | t }}</p>
            <p class="flex flex-col gap-sm c-gray-700" v-html="cartDeliveryModes" />
          </div>
        </div>
        <div v-if="orderResponse?.purchaseOrderNumber">
          <div class="flex flex-col items-start gap-sm">
            <p class="text-base fw-strong">{{ 'Customer PO Number' | t }}</p>
            <p class="customer-po-number c-gray-700">{{ orderResponse?.purchaseOrderNumber }}</p>
          </div>
        </div>
      </div>
    </div>
    <p class="mt-lg text-sm">
      {{ 'Need Help? Contact us' | t }} <a href="mailto:customer.service@sloanmarketplace.com" class="text-sm fw-strong">customer.service@sloanmarketplace.com</a>
      {{ 'or call us toll free' | t }} <a href="tel:18666570023" class="text-sm fw-strong">866.657.0023</a>
    </p>
    <div class="flex items-start justify-end mt-lg">
      <SloButton @click="handleContinue" type="button">
        {{ 'Continue Shopping' | t }}
      </SloButton>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import t from '../../main/i18n';
import {
  getCardNumberLastFourDigits,
  getCardTypeIconName,
  formatOrderNumber,
  tagManagerTrack
} from '../../main/utils';
import SloButton from '../../../lib/components/SloButton.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';

export default {
  name: 'CheckoutConfirmation',
  components: {
    SloButton,
    SloIcon
  },
  filters: {
    t,
    getCardNumberLastFourDigits,
    getCardTypeIconName,
    formatOrderNumber
  },
  props: {
    orderResponse: {type: Object, required: true}
  },
  created() {
    tagManagerTrack('conversion', {
      value: parseFloat(this.orderResponse?.totalPrice?.value.toFixed(2)),
      currency: 'USD'
    });

    tagManagerTrack('purchase', {
      ecommerce: {
        value: parseFloat(this.orderResponse?.subTotal?.value.toFixed(2)),
        tax: parseFloat(this.orderResponse?.totalTax?.value.toFixed(2)),
        shipping: parseFloat(this.orderResponse?.deliveryCost?.value.toFixed(2)),
        transaction_id: this.orderResponse?.code,
        currency: this.orderResponse?.subTotal?.currencyIso,
        user: this.user?.accountNumber || 'guest',
        items: this.orderResponse?.orderEntries.map(cartEntry => ({
          item_id: cartEntry?.product?.code,
          item_name: cartEntry?.product?.number,
          item_category: cartEntry?.product?.type,
          price: cartEntry?.product?.price?.value,
          quantity: cartEntry?.quantity,
          totalPrice: parseFloat(cartEntry?.totalPrice?.value.toFixed(2))
        }))
      }
    });
  },
  computed: mapGetters('marketplace', ['cartDeliveryModes']),
  watch: {
    orderResponse(newValue) {
      if (!newValue) {
        this.$router.replace({name: 'marketplace-cart'});
      }
    }
  },
  methods: {
    async handleContinue() {
      this.$snotify.success(t('Thank you for your order!'));
      await this.$router.replace({name: 'marketplace-cart'});
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../../scss/variables';
@import '../../../scss/system';

.marketplace-checkout--order-confirmation-wrapper {
  padding: 2.4rem;
  border: 1px solid $color-gray-400;
  border-radius: 0.8rem;

  h2 {
    word-break: break-word;
  }
}

.marketplace-checkout--confirmation {
  display: grid;
  gap: 1.6rem;
  margin-top: 3.2rem;

  @media (min-width: $screen-md) {
    grid-template-columns: repeat(2, 1fr);
    gap: 3.2rem;
  }

  .grid-column-full-width {
    grid-column: 1 / -1;
  }

  p {
    margin: 0;
    word-break: break-word;
  }
}
</style>
